<script setup lang="ts">
import type { CSSProperties } from 'vue'
import type { PromptVariant } from '../Card/index.vue'
import type { Prompt, PromptMinified } from '~~/src/types/prompt'
import { isAd } from '~~/src/types/ad'
import type { Elemental } from '~~/src/types/elemental'

interface IProps {
  elementals?: Elemental[] | null
  variant?: PromptVariant | 'preview'
  canLoadMore?: boolean
  loading?: boolean
  scroll?: boolean
  showFeaturedTag?: boolean
  showNoPromptsMessage?: boolean
  style?: CSSProperties
  showUpvoteButton?: boolean
  isSearching?: boolean
  showAddAction?: boolean
  openInNewTab?: boolean
  disableInfinityScroll?: boolean
}

interface IEmits {
  (e: 'loadMore'): void
  (e: 'reachedEnd'): void
  (e: 'addElemental', prompt: Elemental): void
}

const props = withDefaults(defineProps<IProps>(), { showFeaturedTag: true, showUpvoteButton: true, showNoPromptsMessage: true })
const emit = defineEmits<IEmits>()
const { elementals, variant, canLoadMore, loading, scroll, showFeaturedTag, showUpvoteButton, style, isSearching, showAddAction, disableInfinityScroll } = toRefs(props)

const containerRef = ref<HTMLElement>()

if (!props.disableInfinityScroll && props.scroll) {
  useInfiniteScroll(
    containerRef,
    () => {
      if (disableInfinityScroll.value)
        return
      emit('reachedEnd')
    },
    { distance: 100 },
  )
  let observer: ResizeObserver
  whenever(containerRef, (container) => {
    observer = new ResizeObserver(() => {
      if (container.scrollHeight <= container.clientHeight) {
        if (disableInfinityScroll.value)
          return
        emit('reachedEnd')
      }
    })

    observer.observe(container)
  })
  onUnmounted(() => observer?.disconnect())
}
</script>

<template>
  <div h-full>
    <div
      ref="containerRef"
      flex="~ col" gap-2
      :class="[
        scroll ? 'h-max max-h-[316px] overflow-y-auto overflow-x-clip' : 'h-full',
      ]"
      :style="style"
    >
      <TransitionGroup v-if="elementals && elementals.length > 0" name="fade">
        <div v-for="(elemental, index) in elementals" :key="elemental.id">
          <slot v-if="variant === 'preview'" name="preview-prompt" :prompt="elemental" :index="index">
            <PromptPreview
              :prompt="(elemental as PromptMinified | Prompt)"
              :show-add-action="showAddAction"
              :open-in-new-tab="openInNewTab"
              @add-elemental="$emit('addElemental', $event)"
            />
          </slot>

          <template v-else-if="isAd(elemental)">
            <AppPubPrompt />
          </template>

          <PromptCard
            v-else
            :prompt="elemental"
            :variant="variant"
            :show-featured-tag="showFeaturedTag"
            :show-upvote-button="showUpvoteButton"
            :last-item="index === elementals.length - 1"
          />
        </div>
      </TransitionGroup>
      <Text v-if="loading || !elementals?.length" key="not-found" variant="small" text-bluish-softer text-center mt-5 mb-4>
        <slot v-if="!isSearching" name="not-searching">
          Start typing to search.
        </slot>
        <slot v-else-if="loading" name="loading">
          Loading...
        </slot>
        <slot v-else name="not-found">
          No items found.
        </slot>
      </Text>

      <Text
        v-if="!loading && canLoadMore && elementals?.length"
        variant="small" text-center text-bluish-softer hover:text-bluish-darker cursor-pointer mt-5 mb-4
        @click.stop="!loading && $emit('loadMore')"
      >
        Load More
      </Text>
    </div>
  </div>
</template>
